import React, { useState } from "react";
import {
  Box,
  VStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Container,
  useToast,
  Heading,
  Text,
  useColorModeValue,
  Link,
  Divider,
  Select,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { PeerxLogo } from "../../components/icons/Icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthProvider";

const MotionButton = motion(Button);

const ForgotPasswordPage = () => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate();
  const toast = useToast();
  const { firebaseUser, sendPasswordReset } = useAuth();

  const [email, setEmail] = useState(" ");
  const [loading, setLoading] = useState(false);

  const textColor = useColorModeValue("brand.400", "white");
  const bgColor = useColorModeValue("brand.300", "gray.900");
  const cardBgColor = useColorModeValue("brand.100", "gray.700");

  const handleLanguageChange = async (event) => {
    const selectedLanguage = event.target.value;
    i18n.changeLanguage(selectedLanguage);
    try {
      if (firebaseUser?.uid) {
        await firebaseUser.updateProfile({ language: selectedLanguage });
        console.log("Language preference saved successfully.");
      }
    } catch (error) {
      console.error("Failed to save language preference:", error);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      toast({
        title: t("forgotpasswordpage.missing_email_title"),
        description: t("forgotpasswordpage.missing_email_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    /* if (!email.endsWith("@example.com")) {
      toast({
        title: t("forgotpasswordpage.invalid_email_domain_title"),
        description: t("forgotpasswordpage.invalid_email_domain_message"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    } */

    setLoading(true);
    try {
      await sendPasswordReset(email);
      toast({
        title: t("forgotpasswordpage.reset_email_sent_title"),
        description: t("forgotpasswordpage.reset_email_sent_message", { email }),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setTimeout(() => {
        navigate("/auth/sign-in");
      }, 2000);
    } catch (error) {
      if (error.code === "auth/network-request-failed") {
        toast({
          title: t("forgotpasswordpage.network_error_title"),
          description: t("forgotpasswordpage.network_error_message"),
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("forgotpasswordpage.reset_email_failed_title"),
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box py={10} bg={bgColor} minH="100vh">
      <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          width="120px"
          bg="white"
          color="black"
          size="sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>
      <Container maxW="md">
        <Heading mb="20px" size="2xl" fontFamily="Poppins" color="brand.200" fontWeight="bold" letterSpacing="wider" as={RouterLink} to="/">
          <PeerxLogo h="90px" w="160px" />
        </Heading>
        <Box
          as={motion.div}
          bg={cardBgColor}
          borderRadius="lg"
          shadow="lg"
          mt="20px"
          p={8}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <VStack spacing={6} align="stretch">
            <Box textAlign="center" mb={4}>
              <Heading size="lg" color="brand.300">
                {t("forgotpasswordpage.forgot_password_title")}
              </Heading>
              <Text mt={2} color={useColorModeValue("gray.600", "gray.400")}>
                {t("forgotpasswordpage.forgot_password_description")}
              </Text>
            </Box>

            <FormControl isRequired>
              <FormLabel color={textColor}>{t("forgotpasswordpage.email_label")}</FormLabel>
              <Input
                type="email"
                placeholder={t("forgotpasswordpage.email_placeholder")}
                bg={useColorModeValue("gray.50", "gray.600")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
          </VStack>
        </Box>

        <MotionButton
          colorScheme="teal"
          w="full"
          mt={6}
          py={6}
          fontWeight="bold"
          onClick={handlePasswordReset}
          isLoading={loading}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {t("forgotpasswordpage.send_reset_link")}
        </MotionButton>

        <Divider my={6} />

        <Text fontSize="sm" color="gray.500" textAlign="center">
          {t("forgotpasswordpage.remember_password")} {" "}
          <Link as={RouterLink} to="/auth/sign-in" color="brand.200" fontWeight="bold">
            {t("forgotpasswordpage.login_link")}
          </Link>
        </Text>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
